import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { getCatalogId } from '../../../utils/tree/ids';
import { createReferenceEqualSelector } from '../../selectors/utils';
import { setMarket } from '../intl';
import type { RootState } from '../..';
import type { NodeBaseItem } from '../../../entities/Node';

interface TreeSelectionState {
  selectedNodeItem: Record<string, NodeBaseItem>;
  selectedNodeItems: Record<string, NodeBaseItem[]>;
}

const initialState: TreeSelectionState = {
  selectedNodeItem: {},
  selectedNodeItems: {},
};

const treeSelectionSlice = createSlice({
  name: 'treeSelection',
  initialState,
  reducers: {
    resetSelectedNode(state) {
      state.selectedNodeItem = {};
      state.selectedNodeItems = {};
    },
    selectNodes: {
      prepare(
        tree: string,
        selectedNodeItem: NodeBaseItem,
        selectedNodeItems?: NodeBaseItem[],
      ) {
        return {
          meta: { tree },
          error: null,
          payload: {
            selectedNodeItem,
            selectedNodeItems: selectedNodeItems || [],
          },
        };
      },
      reducer(
        state,
        action: PayloadAction<
          { selectedNodeItem: NodeBaseItem; selectedNodeItems: NodeBaseItem[] },
          string,
          { tree: string }
        >,
      ) {
        const {
          meta: { tree },
          payload: { selectedNodeItem, selectedNodeItems },
        } = action;
        const catalogId = getCatalogId(tree);

        state.selectedNodeItem = {
          [catalogId]: selectedNodeItem,
        };

        state.selectedNodeItems = {
          [catalogId]: selectedNodeItems,
        };
      },
    },
    updateSelectedCategoryNode(
      state,
      action: PayloadAction<{
        property: string;
        value: unknown;
        treeId: string;
        isDirty: boolean;
      }>,
    ) {
      const { property, value, treeId, isDirty } = action.payload;
      const catalogId = getCatalogId(treeId);

      if (!state.selectedNodeItem[catalogId]?.node) return;

      const node = state.selectedNodeItem[catalogId].node as {
        category: Record<string, unknown>;
        isDirty: boolean;
        isNew: boolean;
      };

      node.category[property] = value;
      node.isDirty = isDirty;
      node.isNew = !node.category.categoryId;
    },
    updateSelectedItemNode(
      state,
      action: PayloadAction<{
        catalogId: string;
        mainParentDTOs: {
          parentCategoryId?: string;
          parentCategoryName: string | null;
          parentExternalCategoryId: string | null;
          mainParent: boolean;
        }[];
      }>,
    ) {
      const { mainParentDTOs, catalogId } = action.payload;
      if (state.selectedNodeItem[catalogId]?.node?.item) {
        state.selectedNodeItem[catalogId].node.item.mainParentDTOs = mainParentDTOs;
      }
    },
    selectNodeIsDirty(
      state,
      action: PayloadAction<{ tree: string; isDirty: boolean }>,
    ) {
      const { tree, isDirty } = action.payload;
      const catalogId = getCatalogId(tree);
      if (state.selectedNodeItem[catalogId]?.node) {
        state.selectedNodeItem[catalogId].node.isDirty = isDirty;
      }
    },
    clearSelectionForCatalog(state, action: PayloadAction<string>) {
      const catalogId = action.payload;
      delete state.selectedNodeItem[catalogId];
      delete state.selectedNodeItems[catalogId];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setMarket, () => ({ ...initialState }));
  },
});

export const {
  resetSelectedNode,
  selectNodes,
  updateSelectedCategoryNode,
  updateSelectedItemNode,
  selectNodeIsDirty,
  clearSelectionForCatalog,
} = treeSelectionSlice.actions;

export default treeSelectionSlice.reducer;

// Selectors
const selectTreeSelection = (state: RootState) => state.treeSelection;

export const selectSelectedNodeItem = createReferenceEqualSelector(
  selectTreeSelection,
  (state) => state.selectedNodeItem,
);

export const selectSelectedNodeItems = createReferenceEqualSelector(
  selectTreeSelection,
  (state) => state.selectedNodeItems,
);

export const selectSelectedNodeItemForCatalog = createReferenceEqualSelector(
  selectTreeSelection,
  (_state: RootState, catalogId: string) => catalogId,
  (state, catalogId) => state.selectedNodeItem[catalogId] || null,
);

export const selectSelectedNodeItemsForCatalog = createReferenceEqualSelector(
  selectTreeSelection,
  (_state: RootState, catalogId: string) => catalogId,
  (state, catalogId) => state.selectedNodeItems[catalogId] || [],
);
